/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { SVGProps } from 'react';

/**
 * Live NM's mobile-navigation hamburger, transcribed from live's own asset.
 *
 * WHY THIS IS AN ASSET AND NOT A BOX VALUE
 *
 * The box was already right and the DRAWING was wrong, which is why measuring
 * the box reported parity. NM's trigger declares a 20px box and keeps it: what
 * changes here is only what gets painted inside it.
 *
 *     glyph                     box       painted ink        aspect
 *     lucide `Menu` (before)    20 x 20   15.33 x 13.33      1.150
 *     this asset    (after)     20 x 20   20.00 x 14.67      1.358
 *     live NM                   -         20.0  x 14.5       1.379
 *
 * lucide's mark is three round-capped STROKES evenly spread over a square
 * 24-unit grid, so it inks at a constant ~1.15 whatever box it is given — no
 * value of `size-*` reaches 1.358, and no token could have carried this. Live
 * NM's mark is three FILLED rules on a wide, short grid. The fix had to be the
 * drawing.
 *
 * The 20px box needs no measurement of its own precisely because the asset
 * fills its viewBox edge to edge (see the rule table): at `size-5` the
 * letterboxed art resolves to 97/71.43 of the box height, i.e. 20.00 x 14.67,
 * which is live's scanned ink to within a quarter pixel. `mobileMenuGlyphBox`
 * in lib/brand/header-composition.ts therefore stays `size-5`, byte-identical.
 *
 * PROVENANCE — live's own file, not a measurement of it
 *
 * The `<path>` below is byte-identical to the `d` of the inline
 * `data:image/svg+xml;base64` asset on `img.mobile-navigation-hamburger-nav`,
 * decoded from `.reference/homepage.mobile.html` and
 * `.reference/homepage.desktop.html`. Both carry the same 228 base64
 * characters. Provenance was checked INSIDE each file rather than from its
 * name: both declare `<title>Neiman Marcus | Designer Clothing, Shoes,
 * Handbags, & Beauty</title>`.
 *
 * ITS ALT IS `Navigation Menu`, WHICH IS NOT THE HANDLE BG'S ASSET USES. Live
 * BG spells the same control `alt="Mobile navigation icon"`. A search for BG's
 * wording inside NM's dump returns nothing and reads as "NM's glyph was never
 * harvested" — the two brands share the CLASS name and not the alt, so the
 * class is the handle that finds both.
 *
 * Live declares no `width`/`height` on this asset, only the viewBox, so there
 * is no declared box to adopt the way BG's 30px one was adopted; the box comes
 * from the trigger and is unchanged.
 *
 * GEOMETRY, read off the path — three FILLED rectangles, square ends, no caps
 * and no stroke:
 *
 *     rule     x                width    y                 thickness
 *     top      0     -> 97      97       0     ->  14.49   14.49
 *     middle   0.02  -> 96.98   96.96    28.47 ->  42.96   14.49
 *     bottom   0.02  -> 96.98   96.96    56.93 ->  71.42   14.49
 *
 * Ink box 97.000 x 71.420, aspect 1.3582. Gaps 13.98 and 13.97 — even to
 * within 0.01. Thickness identical on all three rules. The 0.02 left offset on
 * the lower two rules is live's own and is reproduced rather than tidied: it is
 * 0.004px at the rendered box, and a transcription that corrects its source
 * stops being evidence of anything.
 *
 * THREE SOURCES AGREE ON THE SHAPE. This asset's aspect is 1.3582. Two earlier
 * agents pixel-scanned live's ink at 20.0 x 14.5 — aspect 1.379 — through a
 * colour-managed canvas off `.reference/live-shots/nm-search-390.png` at DPR 2,
 * recorded in lib/brand/header-composition.ts beside `mobileMenuGlyphBox`. On a
 * mark 14.5 CSS px tall at DPR 2 those two aspects differ by well under one
 * device pixel. Neither reading was derived from the other, and the third
 * instrument — a path parser written for this change — reproduced BG's already
 * published 26.336 x 14.842 exactly before it was pointed at this file.
 *
 * DELIBERATE DIVERGENCE FROM THE SOURCE: `fill="currentColor"`. Live's path
 * declares no fill at all and therefore paints black. Ours inherits the header
 * foreground, which is what the trigger's hover/opacity treatment and every
 * other glyph in this directory already rely on; `BgMenuIcon` beside it makes
 * the same substitution for the same reason. Live's `data-name="hamburger"`
 * attribute is dropped — it is authoring metadata, not geometry, and it is the
 * only thing in the source this file does not carry.
 *
 * WHY THE FILE IS NOT NAMED `nm-menu-icon`. That name would form an
 * `nm-`/`bg-` same-stem pair with `bg-menu-icon.tsx`, which the structural
 * scan in src/__tests__/brand-fork.test.ts reads as one concept implemented
 * twice. These are not two implementations — they are two DRAWINGS, selected
 * by `mobileMenuGlyph` in DRAWER_COMPOSITION, which is exactly the brand-keyed
 * map that gate exists to push work towards. The name comes from the asset's
 * own `data-name="hamburger"`, so it is live NM's word rather than an evasion,
 * but the choice is worth stating plainly: the alternative is a paired name
 * plus a recorded reason in that gate's baseline, which would grow a ratchet
 * for something that is not a fork.
 */
export function NmHamburgerIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 97 71.43" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path d="M0 0h97v14.49H0zm.02 28.47h96.96v14.49H.02zm0 28.46h96.96v14.49H.02z" fill="currentColor" />
        </svg>
    );
}
